if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        this.minQuantity = parseInt(this.dataset.minQuantity, 10) || 2;
        this.discountCents = parseInt(this.dataset.discountCents, 10) || 0;
        this.freeShipping = this.dataset.freeShipping === 'true';
        this.freeShippingMinQuantity = this.freeShipping
          ? parseInt(this.dataset.freeShippingMinQuantity, 10) || this.minQuantity
          : this.minQuantity;

        this.countEl = this.querySelector('[data-bundle-count]');
        this.priceEl = this.querySelector('[data-bundle-price]');
        this.priceOriginalEl = this.querySelector('[data-bundle-price-original]');
        this.priceFinalEl = this.querySelector('[data-bundle-price-final]');
        this.progressEl = this.querySelector('[data-bundle-progress]');
        this.perksEl = this.querySelector('[data-bundle-perks]');
        this.noteEl = this.querySelector('[data-bundle-note]');
        this.viewCartEl = this.querySelector('[data-bundle-view-cart]');
        this.cartCountEl = this.querySelector('[data-bundle-cart-count]');

        this.buildProgressSegments();
        this.refresh();

        // Same cart-update pub/sub every add-to-cart on the site already
        // publishes to (see product-form.js) — keeps this in sync whether
        // the add happened here, in quick add, or in the cart drawer.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      buildProgressSegments() {
        if (!this.progressEl) return;
        // One pill segment per hat needed to unlock every reward — each
        // fills in solid as that many hats land in the cart, instead of
        // one continuous bar, matching a fulfillment-style progress meter.
        // <span>, not <div> — Dawn's base.css hides empty divs by default
        // (`div:empty { display: none }`, meant for empty CMS content),
        // which was silently hiding these decorative segments.
        const segmentCount = Math.max(this.minQuantity, this.freeShippingMinQuantity);
        this.segments = [];
        for (let i = 0; i < segmentCount; i += 1) {
          const segment = document.createElement('span');
          segment.className = 'bundle-builder__progress-segment';
          this.progressEl.appendChild(segment);
          this.segments.push(segment);
        }
      }

      refresh() {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => this.render(cart))
          .catch((error) => console.error(error));
      }

      formatMoney(cents) {
        const value = Math.max(0, cents / 100).toLocaleString('en-PH', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return `₱${value}`;
      }

      render(cart) {
        const quantity = cart.item_count || 0;
        const discountUnlocked = quantity >= this.minQuantity;
        const shippingUnlocked = this.freeShipping && quantity >= this.freeShippingMinQuantity;

        // Whichever reward isn't unlocked yet is the one to nudge toward —
        // the ₱ discount first, then free shipping once that's in hand.
        let nextTarget = null;
        if (!discountUnlocked) {
          nextTarget = this.minQuantity;
        } else if (this.freeShipping && !shippingUnlocked) {
          nextTarget = this.freeShippingMinQuantity;
        }
        const remaining = nextTarget ? nextTarget - quantity : 0;

        if (this.segments) {
          this.segments.forEach((segment, index) => {
            segment.classList.toggle('is-filled', quantity > index);
          });
        }

        if (this.countEl) {
          if (quantity === 0) {
            this.countEl.textContent = 'Add hats to build your bundle';
          } else if (nextTarget) {
            this.countEl.textContent = `${quantity} hat${quantity === 1 ? '' : 's'} selected — add ${remaining} more`;
          } else {
            this.countEl.textContent = `${quantity} hat${quantity === 1 ? '' : 's'} selected`;
          }
        }

        if (this.priceEl) {
          if (quantity > 0) {
            this.priceEl.hidden = false;
            const original = cart.total_price;
            if (discountUnlocked) {
              this.priceOriginalEl.textContent = this.formatMoney(original);
              this.priceOriginalEl.hidden = false;
              this.priceFinalEl.textContent = this.formatMoney(original - this.discountCents);
            } else {
              this.priceOriginalEl.hidden = true;
              this.priceFinalEl.textContent = this.formatMoney(original);
            }
          } else {
            this.priceEl.hidden = true;
          }
        }

        const discountLabel = `₱${(this.discountCents / 100).toFixed(0)} off`;
        const nextRewardLabel = !discountUnlocked ? discountLabel : 'Free shipping';

        if (this.perksEl) {
          // Checkmark pills for whatever's already unlocked, plus one
          // highlighted "next" pill teasing whichever reward is still
          // pending — never both a checkmark AND a teaser for the same
          // reward at once.
          const pills = [];
          if (discountUnlocked) {
            pills.push(`<li class="bundle-builder__pill bundle-builder__pill--unlocked">✓ ${discountLabel}</li>`);
          }
          if (nextTarget) {
            pills.push(
              `<li class="bundle-builder__pill bundle-builder__pill--next">${remaining} more &rarr; ${nextRewardLabel}</li>`
            );
          }
          if (shippingUnlocked) {
            pills.push('<li class="bundle-builder__pill bundle-builder__pill--unlocked">✓ Free shipping</li>');
          }
          this.perksEl.innerHTML = pills.join('');
        }

        if (this.noteEl) {
          if (nextTarget) {
            const hatWord = remaining === 1 ? 'hat' : 'hats';
            this.noteEl.innerHTML = `Add ${remaining} more ${hatWord} for <strong>${nextRewardLabel.toUpperCase()}</strong> — best value`;
            this.noteEl.hidden = false;
          } else {
            this.noteEl.hidden = true;
          }
        }

        if (this.viewCartEl) {
          this.viewCartEl.hidden = quantity === 0;
        }
        if (this.cartCountEl) {
          this.cartCountEl.textContent = quantity;
        }
      }
    }
  );
}
